import dayjs from "dayjs";
import { RegisterEmployeeWizard } from "./RegisterEmployeesWizard.default";
import {
  getRegisterEmployeesTemplate,
  registerCompanyEmployee,
  registerCompanyEmployeesFile,
  registerEmployeesForCompanyWithFilePreview,
} from "../repository/auth.repository";
import { retry } from "../utils/http";
import {
  getCompanies,
  getCompanyDepartments,
  getCompanyAssets,
  getCompanyUserRoles,
  getCountries,
  getLanguages,
} from "../repository/data.repository";
import { nameOf } from "../utils/typescript";
import {
  Company,
  CompanyAsset,
  CompanyRole,
  Department,
  Language,
  Country,
} from "../repository/data.default";
import { QueryOperation } from "../code/search-query/search-query";
import { GenderType } from "../repository/default";
import { InputObject } from "../controls/default";
import { phoneRule } from "../controls/PhoneInput";
import DynamicFormWizardActions, {
  WizardStep,
} from "../controls/DynamicFormWizardActions";

export interface RegisterEmployeesWizardProps {
  readonly open?: boolean;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function RegisterEmployeesWizard(props: RegisterEmployeesWizardProps) {
  // Define request functions for dynamic data
  const companiesRequest = (search: string) => {
    return retry(() =>
      getCompanies({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Company>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  const userRolesRequest = (search: string, formValues: any) => {
    if (!formValues?.company?.value) {
      return Promise.resolve([]);
    }

    return retry(() =>
      getCompanyUserRoles({
        searchQuery: {
          conditions: [
            {
              field: nameOf<CompanyRole>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
            {
              field: nameOf<CompanyRole>("companyUid"),
              operation: QueryOperation.Equal,
              values: [formValues.company.value],
            },
            {
              field: nameOf<CompanyRole>("isManager"),
              operation: QueryOperation.Equal,
              values: [false],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    )
      .then(({ data }) => {
        return data.map((dataItem) => ({
          display: dataItem.name,
          value: dataItem.uid,
          dataItem,
        }));
      })
      .catch((error) => {
        console.error("Error fetching roles:", error);
        return [];
      });
  };

  const departmentsRequest = (search: string, formValues: any) => {
    if (!formValues?.company?.value) {
      return Promise.resolve([]);
    }

    return retry(() =>
      getCompanyDepartments({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Department>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
            {
              field: nameOf<Department>("companyUid"),
              operation: QueryOperation.Equal,
              values: [formValues.company.value],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    )
      .then(({ data }) => {
        return data.map((dataItem) => ({
          display: dataItem.name,
          value: dataItem.uid,
          dataItem,
        }));
      })
      .catch((error) => {
        return [];
      });
  };

  // Optional asset (vessel/aircraft/site) the employee is currently mapped to — scoped to the chosen company.
  const assetsRequest = (search: string, formValues: any) => {
    if (!formValues?.company?.value) {
      return Promise.resolve([]);
    }

    return retry(() =>
      getCompanyAssets({
        searchQuery: {
          conditions: [
            {
              field: nameOf<CompanyAsset>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
            {
              field: nameOf<CompanyAsset>("companyUid"),
              operation: QueryOperation.Equal,
              values: [formValues.company.value],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    )
      .then(({ data }) => {
        return data.map((dataItem) => ({
          display: dataItem.name,
          value: dataItem.uid,
          dataItem,
        }));
      })
      .catch(() => {
        return [];
      });
  };

  const testLanguagesRequest = (search: string) => {
    return retry(() =>
      getLanguages({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Language>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  const countriesRequest = (search: string) => {
    return retry(() =>
      getCountries({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Country>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  const gendersRequest = () => {
    return Promise.resolve(
      Object.entries(GenderType).map(
        ([value, display]) =>
          ({
            display,
            value,
          }) as InputObject,
      ),
    );
  };

  // Define wizard steps
  const steps: WizardStep[] = [
    {
      title: "Option",
      createSchema: (values) => ({
        groups: [
          {
            controls: [
              {
                type: "combobox",
                name: "company",
                label: "Company",
                rules: [
                  {
                    required: true,
                    message: "Company is required",
                  },
                ],
                request: companiesRequest,
              },
            ],
          },
        ],
      }),
    },
    {
      title: "User",
      createSchema: (values) => {
        return {
          groups: [
            {
              name: "employeesInfos",
              label: "Employee",
              isList: true,
              controls: [
                {
                  type: "textbox",
                  name: "userName",
                  label: "User Name (optional)",
                },
                {
                  type: "textbox",
                  name: "firstName",
                  label: "First Name",
                  rules: [
                    {
                      required: true,
                      message: "First Name is required",
                    },
                  ],
                },
                {
                  type: "textbox",
                  name: "middleName",
                  label: "Middle Name (optional)",
                },
                {
                  type: "textbox",
                  name: "lastName",
                  label: "Last Name",
                  rules: [
                    {
                      required: true,
                      message: "Last Name is required",
                    },
                  ],
                },
                {
                  // H4: email is required — otherwise the activation link can't be sent and the account
                  // never activates (previously accepted empty and silently created a dead employee).
                  type: "textbox",
                  name: "email",
                  label: "Email",
                  rules: [
                    {
                      required: true,
                      message: "Email is required",
                    },
                    {
                      type: "email",
                      message: "Invalid email format",
                    },
                  ],
                },
                {
                  type: "phonebox",
                  name: "phoneNumber",
                  label: "Phone Number (optional)",
                  rules: [phoneRule],
                },
                {
                  type: "phonebox",
                  name: "companyPhoneNumber",
                  label: "Company Phone (optional)",
                  rules: [phoneRule],
                },
                {
                  type: "datepicker",
                  name: "dateOfBirth",
                  label: "Date Of Birth",
                  rules: [
                    {
                      required: true,
                      message: "Date Of Birth is required",
                    },
                    {
                      // Reject obviously-nonsense dates: future dates and anything before 1900.
                      validator: (_rule, value) => {
                        if (!value) return Promise.resolve();
                        const d = dayjs(value);
                        if (d.isAfter(dayjs().endOf("day")))
                          return Promise.reject(new Error("Date of Birth cannot be in the future"));
                        if (d.isBefore(dayjs("1900-01-01")))
                          return Promise.reject(new Error("Date of Birth cannot be before 1900"));
                        return Promise.resolve();
                      },
                    },
                  ],
                },
                {
                  type: "combobox",
                  name: "role",
                  label: "Role",
                  rules: [
                    {
                      required: true,
                      message: "Role is required",
                    },
                  ],
                  request: (search) => userRolesRequest(search, values),
                },
                {
                  type: "combobox",
                  name: "department",
                  label: "Department",
                  rules: [
                    {
                      required: true,
                      message: "Department is required",
                    },
                  ],
                  request: (search) => departmentsRequest(search, values),
                },
                {
                  type: "combobox",
                  name: "asset",
                  label: "Asset (optional)",
                  request: (search) => assetsRequest(search, values),
                },
                {
                  // Optional at registration — managers enrich later (progressive profiling;
                  // these drive future personalization, not onboarding/login).
                  type: "combobox",
                  name: "gender",
                  label: "Gender",
                  request: gendersRequest,
                },
                {
                  type: "combobox",
                  name: "testLanguage",
                  label: "Test Language",
                  request: testLanguagesRequest,
                },
                {
                  type: "combobox",
                  name: "country",
                  label: "Country",
                  request: countriesRequest,
                },
                {
                  type: "textbox",
                  name: "externalRefId",
                  label: "External Reference ID",
                },
              ],
            },
          ],
        };
      },
    },
    {
      title: "Finish",
      content: (
        <div className="p-4">
          <p className="mb-2 font-medium">Ready to register.</p>
          <p className="m-0 text-gray-600">
            Click <b>Submit</b> to create the employee account(s) you entered. Use <b>Previous</b> to go back
            and review or change any field. Each new user will receive an activation email with instructions
            to set their password.
          </p>
        </div>
      ),
    },
  ];

  return (
    <DynamicFormWizardActions<RegisterEmployeeWizard>
      open={props.open}
      title="Register Employees"
      steps={steps}
      onFinish={props.onFinish}
      onCancel={props.onCancel}
      showOptionSelection={true}
      optionSelectionText="Do you want to register employees or import an excel file with employees?"
      optionLabels={{
        single: "Register",
        multiple: "Import",
      }}
      fileUploadSupport={true}
      fileUploadText="Create or browse an Excel file with employees"
      fileUploadButtonText="Browse Excel File"
      downloadTemplateSupport={true}
      downloadTemplateButtonText="Download Template"
      fileUploadSizeLimit={100}
      fileUploadErrorText="Excel size limit is 100mb"
      fileUploadAccept=".xls,.xlsx"
      initialValues={{
        company: undefined,
        employeesInfos: [{} as any],
      }}
      apiCalls={{
        submit: async (values) => {
          const input = {
            companyUid: values.company?.value!,
            employeesInfos: values.employeesInfos.map(
              ({
                userName,
                email,
                firstName,
                middleName,
                lastName,
                dateOfBirth,
                role,
                department,
                asset,
                gender,
                testLanguage,
                country,
                externalRefId,
                phoneNumber,
                companyPhoneNumber,
              }) => ({
                userName,
                email,
                firstName,
                middleName,
                lastName,
                dateOfBirth: dateOfBirth.format("YYYY-MM-DD"),
                roleUid: role.value,
                departmentUid: department.value,
                assetUid: asset?.value,
                gender: gender?.value,
                testLanguageUid: testLanguage?.value,
                countryUid: country?.value,
                externalRefId,
                phoneNumber,
                companyPhoneNumber,
              }),
            ),
          };
          return registerCompanyEmployee(input);
        },
        submitFile: async (file, values) => {
          const input = {
            companyUid: values.company?.value!,
            file: file,
          };
          return registerCompanyEmployeesFile(input);
        },
        downloadTemplate: async () => {
          return getRegisterEmployeesTemplate();
        },
        previewFile: async (file, values) => {
          const { company } = values;
          const result = await registerEmployeesForCompanyWithFilePreview({
            file,
            companyUid: company?.value!,
          });

          return {
            ...values,
            employeesInfos: result.employeesInfos.map(
              ({
                email,
                firstName,
                lastName,
                dateOfBirth,
                roleName,
                roleUid,
                departmentName,
                departmentUid,
                assetName,
                assetUid,
              }) => ({
                email,
                firstName,
                lastName,
                dateOfBirth: dayjs(dateOfBirth, ["YYYY-MM-DD"]),
                role: { display: roleName, value: roleUid },
                department: { display: departmentName, value: departmentUid },
                asset: assetUid
                  ? { display: assetName, value: assetUid }
                  : undefined,
              }),
            ),
          };
        },
      }}
      completeButtonText="Complete"
      retryButtonText="Retry"
      closeButtonText="Close"
      resetButtonText="Register Employees"
      errorText="Something went wrong, try again"
    />
  );
}

export default RegisterEmployeesWizard;
